import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowRightLeft, AlertTriangle, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const VendorMassRebidDialog = ({ open, onOpenChange, vendor }) => {
  const { api } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [excluded, setExcluded] = useState(new Set());
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !vendor) {
      setPlan(null);
      setDone(null);
      setNote('');
      setExcluded(new Set());
      return;
    }
    setLoading(true);
    api()
      .post(`/vendors/${vendor.vendor_id}/mass-rebid`, { dry_run: true })
      .then((r) => setPlan(r.data))
      .catch(() => toast.error('Failed to compute re-bid plan'))
      .finally(() => setLoading(false));
  }, [open, vendor, api]);

  const toggleExcluded = (itemId) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleSend = async () => {
    if (!plan || !vendor) return;
    const itemIds = plan.plan
      .filter((r) => r.suggested_vendor && !excluded.has(r.item_id))
      .map((r) => r.item_id);
    if (itemIds.length === 0) {
      toast.error('No items selected for re-bid');
      return;
    }
    if (!window.confirm(`Send ${itemIds.length} re-bid request(s) to the purchaser?`)) return;
    setSending(true);
    try {
      const { data } = await api().post(`/vendors/${vendor.vendor_id}/mass-rebid`, {
        item_ids: itemIds,
        dry_run: false,
        note: note || undefined,
      });
      setDone(data);
      toast.success(`Queued ${data.totals.queued} re-bid request(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mass re-bid failed');
    } finally {
      setSending(false);
    }
  };

  const eligibleCount = plan
    ? plan.plan.filter((r) => r.suggested_vendor && !excluded.has(r.item_id)).length
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vendor-mass-rebid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-orange-600" />
            Mass Re-bid — {vendor?.vendor_name || ''}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-slate-500" data-testid="mass-rebid-loading">
            Computing alternatives…
          </div>
        )}

        {!loading && plan && !done && (
          <div className="space-y-4">
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="mass-rebid-summary">
              {plan.totals.items === 0 ? (
                <span>No items currently use this vendor as preferred supplier.</span>
              ) : (
                <>
                  <span className="font-medium">{plan.totals.items}</span> item(s) supplied by this vendor.
                  <span className="ml-2 text-emerald-700 font-medium">{plan.totals.with_alternative}</span> have a suggested alternative.
                  {plan.totals.low_stock > 0 && (
                    <span className="ml-2 text-orange-700">⚠ {plan.totals.low_stock} are low stock</span>
                  )}
                </>
              )}
            </div>

            {plan.totals.items > 0 && (
              <>
                <div className="overflow-x-auto border border-slate-200 rounded-sm">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">On Hand</TableHead>
                        <TableHead>Suggested Alternative</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plan.plan.map((r) => (
                        <TableRow key={r.item_id} data-testid={`mass-rebid-row-${r.item_id}`} className={!r.suggested_vendor ? 'opacity-60' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={r.suggested_vendor && !excluded.has(r.item_id)}
                              disabled={!r.suggested_vendor}
                              onChange={() => toggleExcluded(r.item_id)}
                              data-testid={`mass-rebid-toggle-${r.item_id}`}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell className="mono">{r.item_sku}</TableCell>
                          <TableCell>
                            {r.item_name}
                            {r.low_stock && (
                              <span className="ml-1 inline-flex items-center gap-1 text-xs text-orange-700">
                                <AlertTriangle className="w-3 h-3" /> low
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right mono text-sm">
                            {r.current_quantity} / {r.reorder_point}
                          </TableCell>
                          <TableCell className="text-sm">
                            {r.suggested_vendor ? (
                              <div>
                                <div className="font-medium text-slate-800">{r.suggested_vendor.vendor_name}</div>
                                <div className="text-xs text-slate-500">
                                  risk {r.suggested_vendor.risk_index} · defect {r.suggested_vendor.defect_rate_pct}% · lead {r.suggested_vendor.lead_time_days}d
                                  {r.suggested_vendor.latest_unit_cost != null && ` · $${r.suggested_vendor.latest_unit_cost}`}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">No alternative found</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Reason for the re-bid, urgency, target ship date…"
                    className="rounded-sm"
                    data-testid="mass-rebid-note"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {!loading && done && (
          <div className="py-6 text-center space-y-2" data-testid="mass-rebid-done">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
            <div className="text-lg font-semibold text-slate-800">
              Queued {done.totals.queued} re-bid request{done.totals.queued === 1 ? '' : 's'}
            </div>
            <div className="text-xs text-slate-500">
              Purchasers will receive an email + in-app notification per item with the suggested alternative.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
            {done ? 'Close' : 'Cancel'}
          </Button>
          {!done && plan && plan.totals.with_alternative > 0 && (
            <Button
              onClick={handleSend}
              disabled={sending || eligibleCount === 0}
              className="bg-orange-600 hover:bg-orange-700 rounded-sm"
              data-testid="mass-rebid-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending…' : `Send ${eligibleCount} re-bid request${eligibleCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VendorMassRebidDialog;
